import { createFileRoute, useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { type AuthState, useAuth } from '@vizit/auth';
import { FEATURE_FLAGS } from '@vizit/constants';
import { getFeatureFlag } from '@vizit/feature-flags';
import { AuthFooter, LoginError, LoginForm, type LoginFormSchema, type SsoLoginError } from '@vizit/organisms';
import * as React from 'react';
import { useAnalytics } from '../../analytics';
import { LoginBrandLayout, LoginPage } from '../../pages';

type LoginSearch = {
  error?: LoginError | SsoLoginError;
  redirect?: string;
};

export const Route = createFileRoute('/_auth/login')({
  staticData: {
    pageName: 'Login',
  },
  component: RouteComponent,
  beforeLoad: async () => {
    const isSsoLoginFlow = await getFeatureFlag(FEATURE_FLAGS.SSO_LOGIN_FLOW, false);
    return { isSsoLoginFlow };
  },
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      error: (search.error as LoginError) ?? undefined,
      redirect: (search.redirect as string) ?? undefined,
    };
  },
});

function RouteComponent() {
  const { error: errorFromSearch, redirect: redirectFromSearch } = useSearch({
    from: Route.id,
  });
  const { isSsoLoginFlow } = useRouteContext({ from: Route.id });

  if (isSsoLoginFlow) {
    return <LoginPage errorFromSearch={errorFromSearch as SsoLoginError} redirectFromSearch={redirectFromSearch} />;
  }

  return <LegacyLogin errorFromSearch={errorFromSearch as LoginError} redirectFromSearch={redirectFromSearch} />;
}

function LegacyLogin({
  errorFromSearch,
  redirectFromSearch,
}: {
  errorFromSearch?: LoginError | null;
  redirectFromSearch?: string;
}) {
  const navigate = useNavigate();
  const { onAuthenticate } = useAuth();
  const { onIdentify } = useAnalytics();

  const [error, setError] = React.useState<LoginError | null>(errorFromSearch ?? null);

  const onSubmit = async ({ email, password }: LoginFormSchema) => {
    const onSuccess = (data: AuthState) => {
      const { userId, organizationId, organizationName } = data;

      if (userId) {
        onIdentify(userId, organizationId, organizationName, email);
      }

      navigate({ to: redirectFromSearch ?? '/' });
    };

    const onError = () => {
      setError(LoginError.InvalidCredentials);
    };

    await onAuthenticate(email, password, onSuccess, onError);
  };

  return (
    <LoginBrandLayout>
      <LoginForm onSubmit={onSubmit} error={error} />
      <AuthFooter />
    </LoginBrandLayout>
  );
}
